import React, { useState } from 'react';
import { Button } from '../../../ui/Button';
import { PhotoUploader } from './Photos/PhotoUploader';
import { PhotoGrid } from './Photos/PhotoGrid';
import { usePhotoContext } from '../contexts/PhotoContext';
import { Globe, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { usePropertyFormContext } from '../contexts/PropertyFormContext';
import { useParams } from 'react-router-dom';

// Logos Facebook et Instagram en SVG
const FacebookLogo = () => (
  <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.407.593 24 1.325 24H12v-9.294H9.294V11.06H12V8.413c0-2.673 1.632-4.13 4.016-4.13 1.14 0 2.118.084 2.402.122v2.788l-1.652.001c-1.296 0-1.547.616-1.547 1.518v1.992h3.094l-.403 3.647H15.22V24h7.455c.73 0 1.325-.593 1.325-1.326V1.326C24 .593 23.407 0 22.675 0z"/>
  </svg>
);

const InstagramLogo = () => (
  <svg className="h-5 w-5 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.849.07 1.366.062 2.633.33 3.608 1.304.974.974 1.242 2.241 1.304 3.607.058 1.265.07 1.645.07 4.849s-.012 3.584-.07 4.849c-.062 1.366-.33 2.633-1.304 3.608-.974.974-2.241 1.242-3.607 1.304-1.265.058-1.645.07-4.849.07s-3.584-.012-4.849-.07c-1.366-.062-2.633-.33-3.608-1.304-.974-.974-1.242-2.241-1.304-3.607C2.175 15.747 2.163 15.367 2.163 12s.012-3.584.07-4.849c.062-1.366.33-2.633 1.304-3.608C4.511 2.493 5.778 2.225 7.144 2.163 8.409 2.105 8.789 2.163 12 2.163zm0-2.163C8.741 0 8.332.012 7.052.07 5.771.127 4.575.384 3.603 1.355c-.972.972-1.228 2.168-1.285 3.449C2.012 6.667 2 7.076 2 12s.012 5.333.07 6.603c.057 1.281.313 2.477 1.285 3.449.972.972 2.168 1.228 3.449 1.285C8.667 23.988 9.076 24 12 24s3.333-.012 4.603-.07c1.281-.057 2.477-.313 3.449-1.285.972-.972 1.228-2.168 1.285-3.449.058-1.27.07-1.679.07-6.603s-.012-5.333-.07-6.603c-.057-1.281-.313-2.477-1.285-3.449C19.08.384 17.884.127 16.603.07 15.333.012 14.924 0 12 0z"/>
    <path d="M12 5.838a6.162 6.162 0 1 0 0 12.324A6.162 6.162 0 0 0 12 5.838zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/>
    <circle cx="18.406" cy="5.594" r="1.44"/>
  </svg>
);

interface PhotosProps {
  onNext: () => void;
}

export const Photos: React.FC<PhotosProps> = ({ onNext }) => {
  const { photos } = usePhotoContext();
  const { formData, handleInputChange } = usePropertyFormContext();
  const { id } = useParams<{ id: string }>();
  const [isSyncingDardz, setIsSyncingDardz] = useState(false);
  const [dardzSyncStatus, setDardzSyncStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [dardzPropertyId, setDardzPropertyId] = useState<number | null>(null);

  const isPublished = formData.publishToDarDz || formData.publishToOuedkniss || formData.publishToFacebook || formData.publishToInstagram || formData.publishExternalSite;

  const handleDardzPublishChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    handleInputChange(e);

    if (isChecked && id) {
      setIsSyncingDardz(true);
      setDardzSyncStatus('idle');

      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

        // ÉTAPE 1 : Sauvegarder publish_dar_dz = 1 en base de données
        const updateResponse = await fetch(`${baseUrl}/properties/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            publish_dar_dz: 1
          }),
        });

        if (!updateResponse.ok) {
          throw new Error('Erreur lors de la mise à jour de publish_dar_dz');
        }

        // ÉTAPE 2 : Synchroniser vers Dardz
        const syncResponse = await fetch(`${baseUrl}/sync-to-dardz/${id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });

        const syncData = await syncResponse.json();

        if (syncResponse.ok && syncData.success) {
          setDardzSyncStatus('success');
          setDardzPropertyId(syncData.dardz_property_id);
          console.log('✅ Bien synchronisé sur Dar-dz.com (ID:', syncData.dardz_property_id, ')');
        } else {
          setDardzSyncStatus('error');
          console.error('❌ Erreur synchronisation Dardz:', syncData.error || 'Erreur inconnue');
        }
      } catch (error) {
        setDardzSyncStatus('error');
        console.error('❌ Erreur lors de la synchronisation avec Dardz:', error);
      } finally {
        setIsSyncingDardz(false);
      }
    }
    else if (!isChecked && id) {
      setIsSyncingDardz(true);
      setDardzSyncStatus('idle');

      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

        // Appeler l'endpoint de dépublication
        const unpublishResponse = await fetch(`${baseUrl}/unpublish-from-dardz/${id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });

        const unpublishData = await unpublishResponse.json();

        if (unpublishResponse.ok && unpublishData.success) {
          setDardzSyncStatus('idle');
          setDardzPropertyId(null);
          console.log('✅ Bien dépublié de Dar-dz.com');
        } else {
          setDardzSyncStatus('error');
          console.error('❌ Erreur dépublication Dardz:', unpublishData.error || 'Erreur inconnue');
        }
      } catch (error) {
        setDardzSyncStatus('error');
        console.error('❌ Erreur lors de la dépublication de Dardz:', error);
      } finally {
        setIsSyncingDardz(false);
      }
    }
  };

  return (
    <div className="space-y-4"> {/* Réduit l'espacement global entre les sections */}
      <div className="grid grid-cols-[1fr,300px] gap-4"> {/* Réduit l'écart entre uploader et cases */}
        <PhotoUploader />
        
        <div className="space-y-3"> {/* Réduit l'espace entre chaque case */}
          {/* DarDz */}
          <div className="rounded-lg border border-gray-200 p-2 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary-500" />
              <label className="flex items-center gap-2 flex-1">
                <input
                  type="checkbox"
                  name="publishToDarDz"
                  checked={formData.publishToDarDz || false}
                  onChange={handleDardzPublishChange}
                  disabled={isSyncingDardz}
                  className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  Publier sur www.dar-dz.com
                </span>
              </label>
              {isSyncingDardz && (
                <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
              )}
              {dardzSyncStatus === 'success' && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
              {dardzSyncStatus === 'error' && (
                <AlertCircle className="h-4 w-4 text-red-500" />
              )}
            </div>
            {dardzSyncStatus === 'success' && dardzPropertyId && (
              <div className="mt-1 text-xs text-green-600 dark:text-green-400 ml-7">
                Synchronisé avec succès (ID Dardz: {dardzPropertyId})
              </div>
            )}
            {dardzSyncStatus === 'error' && (
              <div className="mt-1 text-xs text-red-600 dark:text-red-400 ml-7">
                Erreur lors de la synchronisation
              </div>
            )}
          </div>

          {/* Ouedkniss */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
            <Globe className="h-5 w-5 text-primary-500" />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="publishToOuedkniss"
                checked={formData.publishToOuedkniss || false}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                Publier sur www.ouedkniss.com
              </span>
            </label>
          </div>

          {/* Site externe agence */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
            <Globe className="h-5 w-5 text-emerald-500" />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="publishExternalSite"
                checked={formData.publishExternalSite || false}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                Publier sur mon site
              </span>
            </label>
          </div>

          {/* Facebook */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
            <FacebookLogo />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="publishToFacebook"
                checked={formData.publishToFacebook || false}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                Publier sur Facebook
              </span>
            </label>
          </div>

          {/* Instagram */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
            <InstagramLogo />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="publishToInstagram"
                checked={formData.publishToInstagram || false}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-pink-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                Publier sur Instagram
              </span>
            </label>
          </div>

          {isPublished && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-2 dark:bg-green-900/20 dark:border-green-800">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm text-green-700 dark:text-green-300">
                  Ce bien sera publié sur les sites web
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {photos.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {photos.length}/20 photos
            </span>
          </div>

          <PhotoGrid />
        </>
      )}

      <div className="flex justify-end">
        <Button onClick={onNext}>
          Suivant
        </Button>
      </div>
    </div>
  );
};